import { useGetTimePeriods } from '@shared/api/hooks/reports/periods';
import { InsightsTimePeriod, TimePeriod } from '@shared/api/types';
import { useMemo } from 'react';

type PreviousPeriod = {
  /**
   * Display name of the reporting year before the selected one, e.g. "2024" or
   * "2023/24" for an offset year. Null when there is no earlier year on record.
   */
  name: string | null;
  /** Whether the applied window covers more than one reporting year. */
  spansMultipleYears: boolean;
};

/**
 * Resolves the reporting year immediately before the applied window, for
 * labelling a year-on-year comparison.
 *
 * Takes the boundaries the insights response echoes back, which reflect the
 * window actually applied — an explicit selection or the backend's default of
 * the most recent year with data. A comparison only makes sense within a single
 * reporting year, so a window straddling two reports back as such and callers
 * are expected to drop the comparison.
 */
export function useInsightsPreviousPeriod(
  start?: InsightsTimePeriod | null,
  end?: InsightsTimePeriod | null
): PreviousPeriod {
  const { data: periods } = useGetTimePeriods();

  return useMemo(() => {
    const noComparison = { name: null, spansMultipleYears: false };
    if (!periods || !start || !end) return noComparison;

    const byUuid = new Map(periods.map((period) => [period.uuid, period]));

    // Boundaries are usually month periods, which point at their reporting year
    // through parentUuid; a year boundary is already the year itself.
    const yearOf = (boundary: InsightsTimePeriod): TimePeriod | undefined => {
      const period = byUuid.get(boundary.uuid);
      if (!period) return undefined;
      return period.type === 'year' ? period : byUuid.get(period.parentUuid);
    };

    const startYear = yearOf(start);
    const endYear = yearOf(end);
    if (!startYear || !endYear) return noComparison;
    if (startYear.uuid !== endYear.uuid) {
      return { name: null, spansMultipleYears: true };
    }

    const years = periods
      .filter((period) => period.type === 'year')
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
    const index = years.findIndex((year) => year.uuid === startYear.uuid);

    return {
      name: index > 0 ? years[index - 1].name : null,
      spansMultipleYears: false,
    };
  }, [periods, start, end]);
}
